import { ReactElement } from 'react'

import { useLocation } from 'react-router-dom'

import { userHasDashboardsOnlyPermissions } from 'helpers/featurePermissions'
import { useUserHasPermissions } from 'hooks'
import { RemoteModule } from 'remoteModules/RemoteModule'
import { getPath } from 'routing/pagePaths'
import { useRocketComponentProps } from 'routing/useRocketComponentProps'

// Mounts Rocket's self-contained floating chat popup (`./AiChatPopup`) app-wide.
// The popup portals its own trigger + panel to <body>. We suppress it on the
// `/ai/*` full-page surfaces and for dashboard-only users.
//
// Suppressing on `/ai/*` returns null, which unmounts the whole remote and drops
// the popup's in-memory chat state (an in-flight stream is aborted; a brand-new
// chat that has no server sessionId yet can't be recovered). This is intentional:
// keeping the remote mounted-but-hidden was rejected because it leaves two live
// chat surfaces with divergent state and widens a known double-Toaster issue.
// Once a session exists it's persisted server-side, so Rocket's "See all chats"
// deep-links `/ai/chat/:sessionId` and the full page resumes the same transcript.
export function FloatingChatMount(): ReactElement | null {
    const dashboardOnlyExperience = useUserHasPermissions(
        userHasDashboardsOnlyPermissions
    )
    const { pathname } = useLocation()
    const rocketComponentProps = useRocketComponentProps()

    const aiPath = getPath('aiPage')
    const isAiPage = pathname === aiPath || pathname.startsWith(`${aiPath}/`)

    const enabled = !dashboardOnlyExperience && !isAiPage

    if (!enabled) {
        return null
    }

    return (
        <RemoteModule
            scope="rocket"
            module="./AiChatPopup"
            componentProps={rocketComponentProps}
            fallback={null}
            errorFallback={null}
        />
    )
}
